import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff } from 'lucide-react';

export default function AuthPage() {
  const [tab, setTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/';

  useEffect(() => {
    if (user) navigate(user.role === 'admin' && from === '/admin' ? '/admin' : from, { replace: true });
  }, [user, navigate, from]);

  const handleGoogleAuth = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (tab === 'login') {
        await login(email, password);
      } else {
        if (!name.trim()) { setError('Name is required'); setLoading(false); return; }
        await register(email, password, name);
      }
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left - Editorial Image */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/90f98569-40ac-4edc-a183-4a0b130d6859/images/059abb168abf39e30e694e29fa0e11ae3a10a32ad71568308db06e0fdb8c0763.png"
          alt="HEIR LUXX"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute bottom-12 left-12">
          <h2 className="font-playfair text-4xl text-white font-bold tracking-[0.1em]">HEIR LUXX</h2>
          <p className="text-white/70 text-sm mt-2 font-work tracking-wider">The Personal Shopper Experience</p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-8 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <h1 className="font-playfair text-3xl font-bold text-[#0A0A0A] mb-2">
              {tab === 'login' ? 'Welcome Back' : 'Join HEIR LUXX'}
            </h1>
            <p className="text-sm text-[#999999] font-work">
              {tab === 'login' ? 'Sign in to your personal shopper account' : 'Create your luxury shopping account'}
            </p>
          </div>

          {/* Google Auth */}
          <button
            onClick={handleGoogleAuth}
            className="w-full border border-[#E5E5E5] hover:border-[#D4AF37] transition-colors py-3.5 flex items-center justify-center gap-3 font-work text-sm text-[#0A0A0A] mb-6"
            data-testid="google-auth-btn"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
              <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          <div className="relative flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-[#E5E5E5]" />
            <span className="text-xs text-[#CCCCCC] uppercase tracking-widest font-work">or</span>
            <div className="flex-1 h-px bg-[#E5E5E5]" />
          </div>

          {/* Tabs */}
          <div className="flex border-b border-[#E5E5E5] mb-8">
            {['login', 'register'].map(t => (
              <button
                key={t}
                onClick={() => { setTab(t); setError(''); }}
                className={`flex-1 pb-3 text-xs uppercase tracking-[0.2em] font-semibold transition-colors ${tab === t ? 'border-b-2 border-[#D4AF37] text-[#D4AF37]' : 'text-[#AAAAAA] hover:text-[#0A0A0A]'}`}
                data-testid={`tab-${t}`}
              >
                {t === 'login' ? 'Sign In' : 'Register'}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {tab === 'register' && (
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-[#0A0A0A] font-semibold font-work block mb-2">Full Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Your full name"
                  className="w-full border border-[#E5E5E5] focus:border-[#D4AF37] outline-none px-4 py-3 text-sm font-work text-[#0A0A0A] bg-white"
                  data-testid="name-input"
                />
              </div>
            )}

            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#0A0A0A] font-semibold font-work block mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="w-full border border-[#E5E5E5] focus:border-[#D4AF37] outline-none px-4 py-3 text-sm font-work text-[#0A0A0A] bg-white"
                data-testid="email-input"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#0A0A0A] font-semibold font-work block mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full border border-[#E5E5E5] focus:border-[#D4AF37] outline-none px-4 py-3 pr-10 text-sm font-work text-[#0A0A0A] bg-white"
                  data-testid="password-input"
                />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#AAAAAA]">
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-[#D32F2F] text-xs font-work" data-testid="auth-error">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="bg-[#0A0A0A] text-white hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors py-4 text-xs uppercase tracking-widest font-semibold font-work mt-2 disabled:opacity-50"
              data-testid="auth-submit-btn"
            >
              {loading ? 'Please wait...' : (tab === 'login' ? 'Sign In' : 'Create Account')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
